"use client";

import * as React from "react";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type SectionLanguage = "en" | "it";
export default function RegistryIntroduction() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [language, setLanguage] = React.useState<SectionLanguage>("en");
  React.useEffect(() => {
    const savedLanguage = localStorage.getItem("site-language");
    if (savedLanguage === "en" || savedLanguage === "it") {
      setLanguage(savedLanguage);
    }
  }, []);
  React.useEffect(() => {
    const handleStorageChange = () => {
      const savedLanguage = localStorage.getItem("site-language");
      if (savedLanguage === "en" || savedLanguage === "it") {
        setLanguage(savedLanguage);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("focus", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", handleStorageChange);
    };
  }, []);
  const t = {
    en: {
      title: "Registry",
      subtitle: "Our Registries",
      description: "Celebrating our wedding in Rome with our closest family and friends is truly the greatest gift we could ask for. For those who wish to contribute to our future, we have registered at Williams Sonoma and Amazon.",
      williamsSonoma: "Williams Sonoma",
      amazon: "Amazon",
      kitchenHome: "Kitchen, home, and entertaining",
      everydayEssentials: "Everyday essentials and additional gifts",
      button: "View Registry"
    },
    it: {
      title: "Lista Nozze",
      subtitle: "Le Nostre Liste Nozze",
      description: "Celebrare il nostro matrimonio a Roma con la nostra famiglia e i nostri amici più cari è davvero il regalo più grande che potessimo desiderare. Per chi desidera contribuire al nostro futuro, abbiamo creato una lista nozze su Williams Sonoma e Amazon.",
      williamsSonoma: "Williams Sonoma",
      amazon: "Amazon",
      kitchenHome: "Cucina, casa e intrattenimento",
      everydayEssentials: "Essenziali per la casa e altri regali",
      button: "Vedi Lista Nozze"
    }
  }[language];
  return <section id="registry-introduction" className="bg-background py-24 md:py-32 px-6 overflow-hidden flex flex-col items-center justify-center">
      <div ref={ref} className={`max-w-4xl mx-auto text-center flex flex-col items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground tracking-tight mb-6">
          {t.title}
        </h2>

        <p className="font-serif italic text-lg md:text-xl text-muted-foreground mb-6">
          {t.subtitle}
        </p>

        <p className="font-sans text-base md:text-lg text-muted-foreground leading-relaxed mb-10 max-w-2xl">
          {t.description}
        </p>

        <Separator className="w-24 bg-primary h-[1px] mb-10" />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-3xl mt-4">
          <div className="border border-primary/20 bg-card/30 p-8 md:p-10 text-center shadow-sm hover:shadow-md transition-all duration-300">
            <p className="font-serif text-2xl md:text-3xl text-foreground mb-3">
              {t.williamsSonoma}
            </p>

            <p className="font-sans text-sm text-muted-foreground leading-relaxed mb-8">
              {t.kitchenHome}
            </p>

            <Button asChild className="rounded-none px-8 py-6 text-[12px] uppercase tracking-[3px] font-sans border border-primary bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground transition-all">
              <a href="https://www.williams-sonoma.com/registry/8smtvvwsph/registry-list.html" target="_blank" rel="noopener noreferrer">
                {t.button}
              </a>
            </Button>
          </div>

          <div className="border border-primary/20 bg-card/30 p-8 md:p-10 text-center shadow-sm hover:shadow-md transition-all duration-300">
            <p className="font-serif text-2xl md:text-3xl text-foreground mb-3">
              {t.amazon}
            </p>

            <p className="font-sans text-sm text-muted-foreground leading-relaxed mb-8">
              {t.everydayEssentials}
            </p>

            <Button asChild className="rounded-none px-8 py-6 text-[12px] uppercase tracking-[3px] font-sans border border-primary bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground transition-all">
              <a href="https://www.amazon.com/wedding/share/SaraAndMichael26" target="_blank" rel="noopener noreferrer">
                {t.button}
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}